import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, AgentTrendGPTService, ChannelService, Department, DepartmentService, Team, TeamService, User } from 'lib-trend-core';
import { firstValueFrom } from 'rxjs';
import { ChannelRef } from '../../agent-trendgpt-form/agent-trendgpt-form.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs';

export enum IntentionsTypeEnum {
  WEBHOOK = 'WEBHOOK',
  INSTRUCTIONS = 'INSTRUCTIONS',
}

export interface IntentionModalData {
  agentId: string;
  channelRef: Array<ChannelRef>;
  editMode: boolean;
  intention?: any;
}

export interface IntentionModalResult {
  success: boolean;
}

@Component({
    selector: 'app-intention-modal',
    templateUrl: './intention-modal.component.html',
    styleUrl: './intention-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IntentionModalComponent extends AbstractComponent {
  channelRef: Array<ChannelRef> = [];
  departments: Array<Department> = [];
  users: Array<User> = [];
  editMode: boolean = false;
  editingIntentionId: string | null = null;
  saving: boolean = false;

  private currentChannelId?: string;
  private currentDepartmentId?: string;
  private currentUserId?: string;

  override formGroup: FormGroup = new FormGroup({
    channel: new FormControl('', Validators.required),
    department: new FormControl('', Validators.required),
    user: new FormControl(''),
    details: new FormControl('', [Validators.required, Validators.maxLength(512)])
  });

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<IntentionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IntentionModalData,
    public departmentService: DepartmentService,
    public channelService: ChannelService,
    public teamService: TeamService,
    private agentTrendGPTService: AgentTrendGPTService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadChannelsByCompany();
    
    if (this.data) {
      this.channelRef = this.data.channelRef || [];
      this.editMode = this.data.editMode || false;
      
      if (this.editMode && this.data.intention) {
        this.initEditMode(this.data.intention);
      }
    }
  }

  private initEditMode(intention: any): void {
    const channelId = this.extractChannelIdFromUrl(intention.url);
    const { departmentId, userId } = this.extractDepAndUserFromRequestBody(intention.requestBody);
    
    this.editingIntentionId = intention._id;
    this.currentChannelId = channelId ?? undefined;
    this.currentDepartmentId = departmentId ?? undefined;
    this.currentUserId = userId ?? undefined;

    this.formGroup.patchValue({
      channel: channelId ?? '',
      details: intention.details ?? '',
    });

    if (channelId) {
      this.loadDepartmentsByChannel(channelId).then(() => {
        if (departmentId) {
          this.formGroup.patchValue({ department: departmentId });
          this.loadUsersByDepartment(departmentId).then(() => {
            if (userId) {
              this.formGroup.patchValue({ user: userId });
            }
          });
        }
      });
    }
  }

  async onSelectChannel(event: Event): Promise<void> {
    const target = event.target as HTMLSelectElement;
    const channelId = target.value;
    this.currentChannelId = channelId;
    this.formGroup.patchValue({ department: '', user: '' });
    this.users = [];
    await this.loadDepartmentsByChannel(channelId);
  }

  async onSelectDepartment(event: Event): Promise<void> {
    const target = event.target as HTMLSelectElement;
    const departmentId = target.value;
    this.currentDepartmentId = departmentId;
    this.formGroup.patchValue({ user: '' });
    await this.loadUsersByDepartment(departmentId);
  }

  onSelectUser(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.currentUserId = target.value || undefined;
  }

  private loadChannelsByCompany(): void {
    this.channelService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channels: Array<ChannelRef>) => {
          this.channelRef = channels;
        },
        error: () => {
          this.alertService.error(this.t('agents.intentionModal.errorLoadingChannels'));
        }
      });
  }

  private async loadDepartmentsByChannel(idChannel: string): Promise<void> {
    this.departments = [];

    return new Promise((resolve) => {
      this.departmentService.getDepartmentsByChannel(idChannel)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (departments: Array<Department>) => {
            if (!departments.length) {
              this.alertService.error(this.t('agents.intentionModal.noDepartmentsFound'));
            }
            this.departments = departments;
            resolve();
          },
          error: (err: any) => {
            this.alertService.error(
              err?.error?.message ?? this.t('agents.intentionModal.errorLoadingDepartments')
            );
            resolve();
          },
        });
    });
  }

  private async loadUsersByDepartment(idDepartment: string): Promise<void> {
    this.users = [];

    return new Promise((resolve) => {
      this.teamService.getByDepartment(idDepartment)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (teams: Team[]) => {
            if (teams.length > 0) {
              this.users = teams
                .flatMap(team => team.agents)
                .reduce((uniqueAgents, agent) => {
                  if (!uniqueAgents.some(existingAgent => existingAgent._id === agent._id)) {
                    uniqueAgents.push(agent);
                  }
                  return uniqueAgents;
                }, [] as User[])
                .sort((a, b) => a.name.localeCompare(b.name));
            } else {
              this.users = [];
            }
            resolve();
          },
          error: () => {
            this.alertService.error(this.t('agents.intentionModal.errorLoadingUsers'));
            resolve();
          },
        });
    });
  }

  async onSave(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.currentChannelId = this.formGroup.get('channel')?.value;
    this.currentDepartmentId = this.formGroup.get('department')?.value;
    this.currentUserId = this.formGroup.get('user')?.value || undefined;

    const channel = this.channelRef.find(c => c._id === this.currentChannelId);
    if (!channel) {
      this.alertService.error(this.t('agents.intentionModal.channelNotFound'));
      this.saving = false;
      return;
    }

    const webhookUrl = this.buildCallbackUrl(channel);
    const requestBodyTemplate = this.buildRequestBodyTemplate();

    const payload = {
      type: IntentionsTypeEnum.WEBHOOK,
      description: 'Transferir para um Atendente',
      details: this.formGroup.get('details')?.value,
      httpMethod: 'POST',
      url: webhookUrl,
      autoGenerateParams: false,
      autoGenerateBody: false,
      requestBody: requestBodyTemplate,
      fields: [
        { name: 'nome', jsonName: 'nome', type: 'STRING', description: 'nome da lead' },
        { name: 'telefone', jsonName: 'telefone', type: 'STRING', description: 'telefone da lead' }
      ]
    };

    try {
      if (this.editMode && this.editingIntentionId) {
        await firstValueFrom(this.agentTrendGPTService.editIntentionById(this.editingIntentionId, payload));
        this.alertService.success(this.t('agents.intentionModal.intentionUpdatedSuccess'));
      } else {
        await firstValueFrom(this.agentTrendGPTService.createIntentionByAgentId(this.data.agentId, payload));
        this.alertService.success(this.t('agents.intentionModal.intentionCreatedSuccess'));
      }
      
      this.dialogRef.close({ success: true } as IntentionModalResult);
    } catch (error) {
      this.alertService.error(this.t('agents.intentionModal.errorSavingIntention'));
    } finally {
      this.saving = false;
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  isValid(): boolean {
    return this.formGroup.valid;
  }

  private buildCallbackUrl(channel: ChannelRef): string {
    return `${this.trendWebhookUrl}/gptmaker-api/${channel._id}/webhook`;
  }

  private buildRequestBodyTemplate(): string {
    const template = {
      contextId: '{{telefone}}',
      chatName: '{{nome}}',
      phone: '{{telefone}}',
      intention: {
        action: 'TRANSFER_ATTENDANCE',
        department: this.currentDepartmentId,
        user: this.currentUserId ?? null,
      }
    };
    return JSON.stringify(template, null, 2);
  }

  private get trendWebhookUrl(): string {
    return String(this.environment['trendWebhookUrl']);
  }

  private extractChannelIdFromUrl(url?: string | null): string | null {
    if (!url) return null;

    const base = String(this.trendWebhookUrl).replace(/\/+$/, '');
    const re = new RegExp(`^${base.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}/gptmaker-api/([^/]+)/webhook/?$`);
    const m = re.exec(url.trim());
    return m ? m[1] : null;
  }

  private extractDepAndUserFromRequestBody(requestBody: string): { departmentId: string | null; userId: string | null } {
    let departmentId: string | null = null;
    let userId: string | null = null;

    try {
      const parsed = JSON.parse(requestBody);
      departmentId = parsed?.intention?.department ?? null;
      userId = parsed?.intention?.user ?? null;
    } catch {
      const bodyStr = String(requestBody);
      const depMatch = /"department"\s*:\s*"([^"]+)"/.exec(bodyStr);
      const userMatch = /"user"\s*:\s*"([^"]+)"/.exec(bodyStr);
      departmentId = depMatch ? depMatch[1] : null;
      userId = userMatch ? userMatch[1] : null;
    }
    return { departmentId, userId };
  }
}
